import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, orderBy, query, QuerySnapshot, DocumentSnapshot, FirestoreError } from 'firebase/firestore';
import { db } from '@/services/firebase';
import Button from '@/components/ui/Button';
import { ArrowLeftIcon, BanknotesIcon, QrCodeIcon } from '@heroicons/react/24/outline';
import type { Account } from '@/types';

interface FirestoreAccountDoc {
  accountNumber?: string;
  accountType?: string;
  isActive?: boolean;
  trustedSenders?: Array<{ name?: string; urCodeString?: string; urId?: string; urImage?: string }>;
  createdAt?: any;
  lastUpdated?: any;
}

export default function AccountsPage() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Prefer orderBy if the index/field exists; fall back to simple collection
    let qRef: any;
    try {
      qRef = query(collection(db, 'accounts'), orderBy('createdAt', 'desc'));
    } catch {
      qRef = collection(db, 'accounts');
    }

    const unsubscribe = onSnapshot(
      qRef,
      (snapshot: QuerySnapshot) => {
        const list: Account[] = [];
        snapshot.forEach((doc: DocumentSnapshot) => {
          const data = doc.data() as FirestoreAccountDoc;

          // Safe conversions
          const toDate = (val: any): Date => {
            try {
              if (val && typeof val.toDate === 'function') return val.toDate();
              if (val instanceof Date) return val;
              if (typeof val === 'string') return new Date(val);
            } catch {}
            return new Date();
          };

          list.push({
            id: doc.id,
            accountNumber: String(data.accountNumber || ''),
            accountType: String(data.accountType || 'checking'),
            isActive: Boolean(data.isActive),
            trustedSenders: Array.isArray(data.trustedSenders)
              ? data.trustedSenders
                  .filter((s) => s && typeof s === 'object')
                  .map((s) => ({
                    name: String(s.name || ''),
                    urCodeString: s.urCodeString,
                    urId: s.urId,
                    urImage: s.urImage,
                  }))
              : [],
            createdAt: toDate(data.createdAt),
            lastUpdated: toDate(data.lastUpdated),
          });
        });

        setAccounts(list);
        setLoading(false);
      },
      (error: FirestoreError) => {
        console.error('Error fetching accounts:', error);
        setAccounts([]);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  const formatRelativeDate = (date: Date) => {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInWeeks = Math.floor(diffInDays / 7);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    } else if (diffInWeeks < 4) {
      return `${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''} ago`;
    } else if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffInYears} year${diffInYears !== 1 ? 's' : ''} ago`;
    }
  };

  const ActiveBadge = ({ active }: { active: boolean }) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
      active
        ? 'text-green-800 bg-green-100 border-green-200'
        : 'text-gray-700 bg-gray-100 border-gray-200'
    }`}>
      <span className={`w-2 h-2 mr-2 rounded-full ${active ? 'bg-green-600' : 'bg-gray-500'}`} />
      {active ? 'Active' : 'Inactive'}
    </span>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4" />
          <p className="text-gray-600">Loading accounts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container-centered py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeftIcon className="mr-2 h-4 w-4" />
                  Back to Application
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Accounts</h1>
                <p className="text-gray-600">View customer accounts and initiate wire transfers</p>
              </div>
            </div>
            <div className="text-sm text-gray-500">{accounts.length} account{accounts.length !== 1 ? 's' : ''}</div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container-centered py-8">
        {accounts.length === 0 ? (
          <div className="py-12 text-center">
            <div className="mb-4 text-gray-400">
              <BanknotesIcon className="mx-auto h-12 w-12" />
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-900">No accounts found</h3>
            <p className="mb-4 text-gray-600">Approved applicants will have accounts created automatically.</p>
            <Link to="/applicants">
              <Button>Review Applicants</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {accounts.map((acct) => (
              <div key={acct.id} className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between md:gap-4">
                  <div className="flex-1">
                    <div className="mb-4 flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="text-sm text-gray-500">Account Number</div>
                        <div className="font-mono text-lg font-semibold text-gray-900">{acct.accountNumber || '—'}</div>
                        <div className="text-xs text-gray-500 mt-1">Created {formatRelativeDate(acct.createdAt)}</div>
                      </div>
                      <div className="flex-1 md:ml-4">
                        <span className="text-sm font-medium text-gray-500">Trusted Senders:</span>
                        <div className="mt-1 text-sm text-gray-900">
                          {acct.trustedSenders.length === 0 ? (
                            <span className="text-gray-500">None</span>
                          ) : (
                            <ul className="list-inside list-disc space-y-1">
                              {acct.trustedSenders.map((s, idx) => (
                                <li key={idx} className="flex items-center">
                                  <span>{s.name}</span>
                                  {(s.urCodeString || s.urId || s.urImage) && (
                                    <QrCodeIcon className="inline h-4 w-4 ml-1 text-gray-500" />
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0 ml-4">
                        <ActiveBadge active={acct.isActive} />
                      </div>
                    </div>

                    <div className="mt-2">
                      <span className="text-sm font-medium text-gray-500">Account Type:</span>
                      <span className="ml-2 text-sm text-gray-900">{acct.accountType}</span>
                    </div>
                  </div>

                  <div className="mt-4 md:mt-0 flex-shrink-0">
                    <Link to={`/wire-transfers/add?fromAccount=${encodeURIComponent(acct.accountNumber)}`}>
                      <Button className="bg-blue-600 text-white hover:bg-blue-700">
                        Wire Transfer
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


